/**
 * Centralized AWS client management.
 *
 * Uses Cognito Identity Pool temporary STS credentials scoped per user.
 * Requires REACT_APP_IDENTITY_POOL_ID to be set in the environment
 * and Amplify configured with identityPoolId in src/index.js.
 *
 * Usage:
 *   import { getS3Client, getDynamoDBClient } from '../utils/awsClients';
 *   const s3 = await getS3Client();
 *   const dynamoDB = await getDynamoDBClient();
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';

const REGION = 'us-east-1';

// Clock-skew correction. AWS SigV4 rejects signed requests whose timestamp is
// more than 5 min off server time, which breaks the dashboard for anyone with a
// wrong device clock (any timezone — SigV4 is UTC). We let the AWS SDK itself
// correct it: on a clock-skew error the SDK reads the authoritative server time
// from the S3/DynamoDB response `Date` header, sets the offset, and retries.
// Because the dashboard/video flows make a real list/scan request before any
// presign, the offset is corrected from AWS before presigned URLs are signed.
//
// IMPORTANT: do NOT derive the offset from a same-origin/CloudFront `Date`
// header — cached responses return a stale `Date` (the origin time) plus an
// `Age`, so without adding `Age` the computed offset is wrong for everyone and
// breaks signing globally. AWS API response `Date` headers are never cached, so
// `correctClockSkew` is the safe source of truth.
AWS.config.correctClockSkew = true;

// Cached clients and credential expiry
let cachedCredentials = null;
let cachedS3 = null;
let cachedDynamoDB = null;
let credentialsExpiry = 0;

/**
 * Get temporary AWS credentials from Cognito Identity Pool.
 * Credentials are cached and auto-refreshed 60s before expiry.
 */
export async function getCredentials() {
  const now = Date.now();
  if (cachedCredentials && now < credentialsExpiry - 60000) {
    return cachedCredentials;
  }

  const session = await fetchAuthSession({
    forceRefresh: cachedCredentials ? now >= credentialsExpiry - 60000 : false,
  });
  const { credentials } = session;

  if (!credentials) {
    throw new Error(
      'Identity Pool credentials unavailable – ensure REACT_APP_IDENTITY_POOL_ID is set and user is signed in'
    );
  }

  cachedCredentials = new AWS.Credentials({
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
    sessionToken: credentials.sessionToken,
  });

  credentialsExpiry = credentials.expiration
    ? new Date(credentials.expiration).getTime()
    : now + 3600000;

  // Invalidate cached clients so they pick up new credentials
  cachedS3 = null;
  cachedDynamoDB = null;

  return cachedCredentials;
}

/**
 * Get an S3 client with the current credentials.
 */
export async function getS3Client() {
  const creds = await getCredentials();

  if (!cachedS3) {
    cachedS3 = new AWS.S3({ region: REGION, credentials: creds });
  }

  return cachedS3;
}

/**
 * Get a DynamoDB DocumentClient with the current credentials.
 */
export async function getDynamoDBClient() {
  const creds = await getCredentials();

  if (!cachedDynamoDB) {
    cachedDynamoDB = new AWS.DynamoDB.DocumentClient({ region: REGION, credentials: creds });
  }

  return cachedDynamoDB;
}

/**
 * Clear cached clients and credentials. Call on sign-out.
 */
export function clearAWSClients() {
  cachedCredentials = null;
  cachedS3 = null;
  cachedDynamoDB = null;
  credentialsExpiry = 0;
}
